import {
  FYDEOS_AI_PROVIDER_NAME,
  FYDEOS_NOTES_PRODUCT,
  getCurrentFydeOSBrandLinks,
} from '@affine/component/product-brand';
import { I18n } from '@affine/i18n';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { scrollbarStyle } from '@blocksuite/affine/shared/styles';
import { unsafeCSSVarV2 } from '@blocksuite/affine/shared/theme';
import { type EditorHost } from '@blocksuite/affine/std';
import { InformationIcon, ToggleDownIcon } from '@blocksuite/icons/lit';
import { signal } from '@preact/signals-core';
import { baseTheme } from '@toeverything/theme';
import { css, html, LitElement, nothing, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';

import {
  AIAppEvents,
  type AIError,
  PaymentRequiredError,
  SelectedSourcesFailedError,
  SelectedSourcesLimitExceededError,
  SelectedSourcesProcessingError,
  SelectedSourcesUnavailableError,
  UnauthorizedError,
} from '../provider';

export class AIErrorWrapper extends SignalWatcher(WithDisposable(LitElement)) {
  static override styles = css`
    .error-wrapper {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      gap: 8px;
      align-self: stretch;
      border-radius: 4px;
      padding: 8px 8px 12px 8px;
      background-color: ${unsafeCSSVarV2('aI/errorBackground')};
      font-family: ${unsafeCSS(baseTheme.fontSansFamily)};

      .content {
        align-items: flex-start;
        display: flex;
        gap: 8px;
        align-self: stretch;
        color: ${unsafeCSSVarV2('aI/errorText')};
        font-feature-settings:
          'clig' off,
          'liga' off;
        /* light/sm */
        font-size: var(--affine-font-sm);
        font-style: normal;
        font-weight: 400;
        line-height: 22px; /* 157.143% */

        .icon svg {
          position: relative;
          top: 3px;
        }
      }

      .text-container {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .detail-container {
        display: flex;
        flex-direction: column;
        gap: 4px;
        width: 100%;
      }
      .detail-title {
        display: flex;
        align-items: center;
      }
      .detail-title:hover {
        cursor: pointer;
      }
      .detail-content {
        padding: 8px;
        border-radius: 4px;
        background-color: ${unsafeCSSVarV2('aI/errorDetailBackground')};
        overflow: auto;
      }
      ${scrollbarStyle('.detail-content')}

      .toggle {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .toggle.up svg {
        transform: rotate(180deg);
        transition: all 0.2s ease-in-out;
      }

      .action {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        width: 100%;
      }
      .action-button {
        cursor: pointer;
        color: ${unsafeCSSVarV2('text/primary')};
        background: ${unsafeCSSVarV2('button/secondary')};
        border-radius: 8px;
        border: 1px solid ${unsafeCSSVarV2('button/innerBlackBorder')};
        padding: 4px 12px;
        font-size: var(--affine-font-xs);
        font-style: normal;
        font-weight: 500;
        line-height: 20px;
      }
      .action-button:hover {
        transition: all 0.2s ease-in-out;
        background-image: linear-gradient(
          rgba(0, 0, 0, 0.04),
          rgba(0, 0, 0, 0.04)
        );
      }
    }
  `;

  private readonly _showDetailContent = signal(false);

  protected override render() {
    return html` <div class="error-wrapper">
      <div class="content">
        <div class="icon">${InformationIcon()}</div>
        <div class="text-container">
          <div>${this.text}</div>
          ${
            this.showDetailPanel
              ? html`<div class="detail-container">
                  <div
                    class="detail-title"
                    @click=${() =>
                      (this._showDetailContent.value =
                        !this._showDetailContent.value)}
                  >
                    <span>Show detail</span>
                    <span
                      class="toggle ${
                        this._showDetailContent.value ? 'down' : 'up'
                      }"
                    >
                      ${ToggleDownIcon({ width: '16px', height: '16px' })}
                    </span>
                  </div>
                  ${
                    this._showDetailContent.value
                      ? html`<div class="detail-content">
                          ${this.errorMessage}
                        </div>`
                      : nothing
                  }
                </div>`
              : nothing
          }
        </div>
      </div>
      ${
        this.showAction
          ? html`<div class="action">
              <span
                class="action-button"
                @click=${this.onClick}
                data-testid="ai-error-action-button"
              >
                ${this.actionText}
                ${
                  this.actionTooltip
                    ? html`<affine-tooltip tip-position="top">
                        ${this.actionTooltip}
                      </affine-tooltip>`
                    : nothing
                }
              </span>
            </div>`
          : nothing
      }
    </div>`;
  }

  @property({ attribute: false })
  accessor text: string = '';

  @property({ attribute: false })
  accessor onClick: () => void = () => {};

  @property({ attribute: false })
  accessor errorMessage: string = '';

  @property({ attribute: false })
  accessor actionText: string = 'Contact us';

  @property({ attribute: false })
  accessor actionTooltip: string = '';

  @property({ attribute: false })
  accessor showDetailPanel: boolean = false;

  @property({ attribute: false })
  accessor showAction: boolean = true;

  @property({ attribute: 'data-testid', reflect: true })
  accessor testId = 'ai-error';
}

const PaymentRequiredErrorRenderer = () => html`
  <ai-error-wrapper
    .text=${`You've reached today's ${FYDEOS_AI_PROVIDER_NAME} use limit. Choose a workspace BYOK model or try again after the daily reset.`}
    .showAction=${false}
  ></ai-error-wrapper>
`;

const LoginRequiredErrorRenderer = (host?: EditorHost | null) => html`
  <ai-error-wrapper
    .text=${`Sign in to ${FYDEOS_NOTES_PRODUCT} to continue using AI.`}
    .actionText=${'Log in'}
    .onClick=${() => AIAppEvents.requestLogin.next({ host })}
  ></ai-error-wrapper>
`;

type ErrorProps = {
  text?: string;
  errorMessage?: string;
  actionText?: string;
  actionTooltip?: string;
  onClick?: () => void;
  showAction?: boolean;
};

const generalErrorText =
  'An error occurred, If this issue persists please let us know.';

const GeneralErrorRenderer = (props: ErrorProps = {}) => {
  const feedbackUrl = getCurrentFydeOSBrandLinks().feedback;
  const contactSupport = () =>
    window.open(feedbackUrl, '_blank', 'noopener,noreferrer');

  return html`<ai-error-wrapper
    .text=${props.text ?? generalErrorText}
    .errorMessage=${props.errorMessage ?? ''}
    .showDetailPanel=${!!props.errorMessage}
    .actionText=${props.actionText ?? 'Contact us'}
    .actionTooltip=${props.actionTooltip ?? feedbackUrl}
    .onClick=${props.onClick ?? contactSupport}
    .showAction=${props.showAction ?? true}
  ></ai-error-wrapper>`;
};

export function AIChatErrorRenderer(
  error: AIError,
  host?: EditorHost | null,
  retry?: () => void
) {
  if (error instanceof PaymentRequiredError) {
    return PaymentRequiredErrorRenderer();
  } else if (error instanceof UnauthorizedError) {
    return LoginRequiredErrorRenderer(host);
  } else if (error instanceof SelectedSourcesProcessingError) {
    return GeneralErrorRenderer({
      text: I18n['com.affine.ai.error.selectedSourcesProcessing'](),
      actionText: I18n['com.affine.ai.error.retry'](),
      actionTooltip: '',
      onClick: retry,
      showAction: !!retry,
    });
  } else if (error instanceof SelectedSourcesFailedError) {
    return GeneralErrorRenderer({
      text: I18n['com.affine.ai.error.selectedSourcesFailed'](),
      actionText: I18n['com.affine.ai.error.retry'](),
      actionTooltip: '',
      onClick: retry,
      showAction: !!retry,
    });
  } else if (error instanceof SelectedSourcesUnavailableError) {
    return GeneralErrorRenderer({
      text: I18n['com.affine.ai.error.selectedSourcesUnavailable'](),
      actionText: I18n['com.affine.ai.error.retry'](),
      actionTooltip: '',
      onClick: retry,
      showAction: !!retry,
    });
  } else if (error instanceof SelectedSourcesLimitExceededError) {
    return GeneralErrorRenderer({
      text: I18n['com.affine.ai.error.selectedSourcesLimitExceeded'](),
      showAction: false,
    });
  } else {
    return GeneralErrorRenderer({
      errorMessage: error.message,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ai-error-wrapper': AIErrorWrapper;
  }
}
